/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 9 hook(s) need dependency review; fix each and remove this directive */
import { getBackendUrl } from "@Utils";
import type React from "react";
import { useTranslation, untranslated } from "@i18n";
import { fetchRevisionSeries } from "../../features/files/components/FileVersion/revisionSeriesShared";
import {
  getEffectiveSequenceRules,
  type RevisionSequenceRules,
  type RevisionSequenceType,
} from "../../../../helpers/revisionPattern";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ComponentProps } from "react";
import * as XLSX from "xlsx";
import { BUTTON_TYPES } from "../../cubeComponent/constants/globalVariable";
import ButtonComponent from "../../cubeComponent/form/ButtonComponent";
import ModalStateDialogComponent from "../../cubeComponent/html/modal/ModalWithState";
import { importScheduleTasks } from "../../actions/task";
import { useParams } from "react-router-dom";
import { showToastNotification } from "../../reducers/toastSlice";
import {
  SOMETHING_WENT_WRONG,
  TOAST_TYPE_ERROR,
  TOAST_TYPE_SUCCESS,
  TOAST_TYPE_WARNING,
} from "../../services/constants";
import { useDispatch, useSelector } from "react-redux";
import AutoComplete from "../../cubeComponent/Mui_Form/AutoComplete";
import TextFieldComponent from "../../cubeComponent/Mui_Form/TextFieldComponent";
import axiosWrapper from "../../services/axiosWrapper";
import type { GetFormTemplateResponse } from "@ServerTypes/projects/project.schema";
import Radio from "@mui/material/Radio";
import FormControlLabel from "@mui/material/FormControlLabel";
import RadioGroup from "@mui/material/RadioGroup";

import SlickGrid1, { withDynamicColumns } from "../../cubeComponent/html/SlickGrid1";
import {
  getPurchaseUnits,
  importCostDatabaseItemsFromExcelSheet,
  saveEstimateItemsFromExcelSheet,
} from "../../actions/estimation";
import ToggleSwitchComponent from "../../cubeComponent/form/ToggleSwitchComponent";
import ToolTipComponent from "../../cubeComponent/html/tooltip";
import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import {
  dateTypeDatasource,
  excelImportSources,
  excludeAutoMatchingColumns,
  formatDateFieldValue,
  getExampleFields,
  getGridColumns,
  getMatchDropdownDataSource,
  getRequiredFields,
  getSampleFile,
  parseCsvText,
  saveContainers,
  scheduleDropDownDataSource,
  validateAssetData,
  validateEstimateData,
  validateScheduleData,
  wbsPathTypeDatasource,
  xmlDropdownDatasource,
  DOCUMENT_NUMBER_DUPLICATE_IN_IMPORT_ERROR,
} from "./ExcelHelper";
import type {
  ExcelColumnMapper,
  ExcelExpectedField,
  ExcelFormTemplate,
  ExcelGridColumn,
  ExcelImportCurrentData,
  ExcelImportRow,
  ExcelMatchOption,
  ExcelRequiredField,
  ExcelRow,
  PurchaseUnitOption,
} from "./ExcelHelper";
import type { FormTemplateField } from "@ServerTypes/forms/forms.schema";
import { objectIdString } from "@Helpers/schemas/common.schema";
import type { ColumnDefinition, GridRowBase, SlickGrid1Ref } from "@CubeComponents/html/SlickGrid1";

/**
 * A row as the import grids see it: the dynamic sheet/validation keys plus the identity fields
 * SlickGrid reads off every row. Written as a `type` so the index signature satisfies the weak
 * `GridRowBase` constraint.
 */
type ImportGridRow = GridRowBase & Record<string, unknown>;

/** Formatter bound to this grid's row type and to string column keys. */
type ImportGridFormatter = NonNullable<ColumnDefinition<ImportGridRow, string>["formatter"]>;

/**
 * The import grids' column shape — exactly the members `getColumnDefinitions` sets. Spelled out as a
 * type literal rather than as an intersection with the `ColumnDefinition` INTERFACE so that it keeps
 * an implicit index signature and can go through `withDynamicColumns()`, SlickGrid1's brander for
 * column lists whose `field` keys are only known at runtime (an interface gets no such index
 * signature — see the same note in Charges.tsx). `exportCustomFormatter` and `headerFormatter` are
 * assigned below but SlickGrid1 declares neither — it reads `_excelValueGetter` and
 * `_customHeaderRenderer` — so both are dead properties. Named here rather than renamed, which would
 * change behaviour.
 */
type ImportGridColumn = {
  id: string;
  field: string;
  name: string;
  minWidth: number;
  formatter?: ImportGridFormatter;
  _valueGetter?: (row: ImportGridRow) => unknown;
  exportCustomFormatter?: ImportGridFormatter;
  headerFormatter?: ImportGridFormatter;
};

/**
 * One work-time span in an imported schedule XML: P6 writes Start/Finish, MSP FromTime/ToTime.
 * Either dialect may store a single span or an array of them under the same key — the code branches
 * on `.length` — so the value is viewed as both at once rather than narrowed.
 */
type XmlWorkTime = {
  Start?: string;
  Finish?: string;
  FromTime?: string;
  ToTime?: string;
  "@_xsi:nil"?: unknown;
};
type XmlWorkTimeValue = XmlWorkTime & XmlWorkTime[];

/**
 * One calendar of an imported schedule XML, P6 and MSP branches merged. Nothing ever populates it:
 * `handleConvert` leaves `json` at `{}` on the xml branch, so `json.calendar` is always undefined.
 */
type XmlCalendar = {
  Name?: string;
  HolidayOrExceptions?: { HolidayOrException: { Date?: string; WorkTime: XmlWorkTime }[] };
  StandardWorkWeek?: { StandardWorkHours: { DayOfWeek?: string; WorkTime: XmlWorkTimeValue }[] };
  WeekDays?: {
    WeekDay: {
      DayType: number;
      DayWorking: number;
      TimePeriod?: unknown;
      WorkingTimes: { WorkingTime: XmlWorkTimeValue };
    }[];
  };
};

/**
 * An entry of the calendar / week-timings `SelectDropDown` datasources built from that XML. `id` is
 * the entry's position in the array it was built from, as a STRING: `SelectDropDown` declares a menu
 * item's id that way (`ActionMenuItem`), and numbering it made every use of these two datasources —
 * both `items` props and both `select` handlers — go through an assertion.
 */
type XmlDropdownItem = { text: string; id: string };

/** The bag of options the five grid render helpers below are called with. */
type ImportGridProps = {
  dataSource?: ExcelImportRow[] | string[][] | null;
  columnNames?: ExcelRequiredField[] | null;
  isValidateState?: boolean;
  source?: string;
  dateTimeFields?: string[];
  dateTimeFieldsMap?: Record<string, string | undefined>;
  rowSelected?: boolean;
  allowExport?: boolean;
  height?: number;
};

/**
 * The parsed upload. The xml branch of `handleConvert` never populates it (it is left at `{}`), and
 * the csv/xlsx branches replace it with the sheet's positional rows — so it is viewed as both.
 */
type ParsedUpload = string[][] & { calendar?: XmlCalendar[]; data?: ExcelImportRow[] };

/** The per-row outcome the container import returns, one entry per submitted row. */
type ContainerImportResult = { status?: boolean; message?: string; data?: { fileId?: string } };

/** A container the backend accepted, paired with the row it was created from. */
type CreatedContainer = {
  container: ExcelImportRow;
  response: ContainerImportResult;
  index: number;
};

/** One container to create or match during a direct import, built from a validated row. */
type ContainerDefinition = {
  /** The sheet's File Name cell, converted where the row is read — the file-upload slice this is
   *  handed to (`ExcelImportContainer`) declares it a string. */
  name: string;
  fileFormat: unknown;
  folderPath: string;
  spaceId?: string;
  containerData: ExcelImportRow;
  revisionLabel: string;
  revisionSeriesId: unknown;
  previousLabel?: unknown;
  existingLabels?: unknown;
  startPattern?: string | null;
  sequenceType?: RevisionSequenceType | null;
  sequenceRules?: Required<RevisionSequenceRules> | null;
};

/**
 * SCHEMA GAP: the five import endpoints (`saveEstimateItemsFromExcelSheet`, `importScheduleTasks`,
 * `importCostDatabaseItemsFromExcelSheet`, `assets/importAssetsFromExcel`, `importExcelContainers`)
 * share no response schema; the fields `handleNextClick` actually reads are named here.
 */
type ImportResponse = {
  data: {
    status?: boolean;
    message?: string;
    errorMessage?: string;
    insertedCount?: number;
    data?: ContainerImportResult[];
  };
};
import { formCategories, MAX_EXCEL_IMPORT_CONTAINERS, isDocumentNumberField, getFileExtension } from "@Helpers";
import {
  importExcelContainers,
  maximiseFileUpload,
  setFileUploadProperties,
  setCurrentFileUploadProperties,
  fetchFilesFromFolder,
  ensureFolderPathExists,
  extractDocumentNumber,
} from "../../actions/fileUpload";

import IconComponent from "@CubeComponents/icons";
import { portalCreateRoot as createRoot } from "@CubeComponents/html/cellPortals";
import type { ImportAssetsFromExcelResponse } from "@ServerTypes/assets/assets.schema";
import { getTemplates } from "@Actions/Forms";
import { RootState } from "@Reducers/index";
import type { AppDispatch } from "@Reducers/index";

/**
 * `getRequiredFields` yields plain header strings for the schedule/estimate/cost-database sources and
 * field descriptors for files/assets (see ExcelHelper); this is what tells the two arms apart.
 */
const isExpectedField = (field: ExcelRequiredField): field is ExcelExpectedField => typeof field !== "string";

/** A plain object seen through its keys — how every unvalidated row or bag below is read. */
const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** Reads a `label` off an unvalidated revision-series bag on a file record. */
const readLabel = (value: unknown): string | undefined =>
  isRecord(value) && typeof value.label === "string" ? value.label : undefined;

/**
 * One holiday span in the shape `importScheduleTasks` declares. The rows come either from the
 * workbook's second sheet (whatever columns it happens to carry) or from the XML calendar
 * exceptions, so they are checked rather than assumed.
 */
const isHolidaySpan = (row: unknown): row is { FromDate: string; ToDate: string } =>
  isRecord(row) && typeof row.FromDate === "string" && typeof row.ToDate === "string";

/**
 * A schedule date cell reaches the grid as an Excel serial number, an ISO string or a Date. `NaN`
 * keeps the Invalid Date anything else already produced, without claiming a type the value lacks.
 */
const toDateInput = (value: unknown): number | string | Date =>
  typeof value === "number" || typeof value === "string" || value instanceof Date ? value : NaN;

/**
 * `SlickDataView.getItemById()` is declared to return the row, but returns `undefined` for an id it
 * does not hold — SlickGrid1 keeps a `dataViewItemById` of its own for the same reason. Reading
 * through an annotated return type is what keeps the caller's guard meaningful; assigning the call
 * into a `T | undefined` const does not, because control flow re-narrows a const to its initialiser.
 */
const gridItemById = (
  dataView: SlickGrid1Ref<ImportGridRow>["dataView"],
  id: string | number,
): ImportGridRow | undefined => dataView.getItemById(id);

/**
 * The two dataset shapes these grids are handed. The "Select Header" step feeds SlickGrid the
 * sheet's POSITIONAL cell arrays — its columns are keyed by position (`Object.keys(allJsonData[0])`)
 * — and such a row satisfies `ImportGridRow` at runtime, since SlickGrid only ever indexes a row by
 * column key; an array type simply carries no string index signature for TypeScript to see it.
 * Stated once here instead of at each of the five grids.
 */
const toGridRows = (dataSource: ImportGridProps["dataSource"]): ImportGridRow[] | undefined => {
  if (dataSource == null) return undefined;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- positional cell arrays, see the doc comment above
  return dataSource as ImportGridRow[];
};

/**
 * `importExcelContainers` (actions/fileUpload.ts) is declared `Promise<unknown>` — the only one of
 * the five import calls that is — so its body is checked here instead of being asserted into shape.
 * Every field `handleNextClick` reads is optional on `ImportResponse`, so a body missing one of them
 * still lands in the existing branches.
 */
const toImportResponse = (body: unknown): ImportResponse | undefined => {
  if (!isRecord(body) || !isRecord(body.data)) return undefined;
  const payload = body.data;
  return {
    data: {
      status: typeof payload.status === "boolean" ? payload.status : undefined,
      message: typeof payload.message === "string" ? payload.message : undefined,
      errorMessage: typeof payload.errorMessage === "string" ? payload.errorMessage : undefined,
      insertedCount: typeof payload.insertedCount === "number" ? payload.insertedCount : undefined,
      data: Array.isArray(payload.data) ? payload.data : undefined,
    },
  };
};

/**
 * The five import flows this dialog serves; `source` is one of `excelImportSources`' values.
 * `sourceId` is the schedule / estimate / cost-database / project id the rows are imported into,
 * and is a placeholder for the container (files) flow.
 */
type ExcelImportProps = {
  source: string;
  sourceId?: string;
  showImportModal: boolean;
  setShowImportModal: (value: boolean) => void;
  refreshDataGrid: (reload: boolean, insertedCount?: number) => void;
  currentData?: ExcelImportCurrentData | null;
  /**
   * `null` before the user picks a mode — that is the initial state `useFileManager` holds, so the
   * null is part of the contract rather than an absent prop.
   */
  importMode?: "container" | "file" | null;
};

function ExcelImport({
  source,
  sourceId,
  showImportModal,
  setShowImportModal,
  refreshDataGrid,
  currentData,
  importMode,
}: ExcelImportProps) {
  const { t } = useTranslation();
  const [file, setFile] = useState<File | null>(null);
  /** Every sheet row as a positional cell array, before a header row has been chosen. */
  const [allJsonData, setAllJsonData] = useState<string[][] | null>(null);
  const [filteredJsonData, setFilteredJsonData] = useState<ExcelRow[] | null>(null);
  const [enableNextButton, setEnableNextButton] = useState(false);
  const estimateData = useSelector((state: RootState) => state.estimateData);
  const matchByDocumentNumber = useSelector(
    (state: RootState) => !!state.layout.FILE_MANAGER?.MATCH_BY_DOCUMENT_NUMBER,
  );
  const [purchaseUnitsList, setPurchaseUnitsList] = useState<PurchaseUnitOption[]>([]);
  const [customFields, setCustomFields] = useState<ExcelFormTemplate[]>([]);
  const [projectLevelFields, setProjectLevelFields] = useState<FormTemplateField[]>([]);
  const [allColumnNames, setAllColumnNames] = useState<string[]>([]);
  const [wbsPathTypeIndex, setWbsPathTypeIndex] = useState<number | null>(null);
  const [startDateTypeFormat, setStartDateTypeFormat] = useState<string | null>(null);
  const [finishDateTypeFormat, setFinishDateTypeFormat] = useState<string | null>(null);
  const [headerIndex, setHeaderIndex] = useState<number | null>(null);
  const [selectedHeaderRowId, setSelectedHeaderRowId] = useState<string | null>(null);
  const [finalData, setFinalData] = useState<ExcelImportRow[]>([]);
  const [showOnlyErrors, setShowOnlyErrors] = useState(true);
  const [currentTab, setCurrentTab] = useState("Upload File");
  const [columnMapper, setColumnMapper] = useState<ExcelColumnMapper>({});
  const [notValidPredecessors] = useState<unknown[]>([]);
  const [estimateIcorrectData, setEstimateIcorrectData] = useState<ExcelImportRow[]>([]);
  const [scheduleIcorrectData, setScheduleIcorrectData] = useState<ExcelImportRow[]>([]);
  const [isEstimateDataValid, setIsEstimateDataValid] = useState(true);
  const [isScheduleDataValid, setIsScheduleDataValid] = useState(true);
  const [isXml, setIsXml] = useState(false);
  const [xmlSoftware, setXmlSoftware] = useState("P6");
  const [holidays, setHolidays] = useState<unknown[] | null>(null);
  const [workWeek, setWorkWeek] = useState(["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"]);
  const [workTimings, setWorkTimings] = useState([{ from: 0, to: 24 }]);
  const [calendarArray, setCalendarArray] = useState<XmlCalendar[] | null>(null);
  const [calendarDropdownDatasource, setCalendarDropdownDatasource] = useState<XmlDropdownItem[]>([]);
  const [selectedCalendar, setSelectedCalendar] = useState("");
  const [timingsDropdownDatasource, setTimingsDropdownDatasource] = useState<XmlDropdownItem[]>([]);
  const [workTimingsArray, setWorkTimingsArray] = useState<{ from: number; to: number }[][]>([]);
  const [workTimingsForDropDown, setWorkTimingsForDropdown] = useState("");
  const getInitialUploadOption = () => {
    if (importMode === "container") return "complete";
    if (importMode === "file") return "importDirectly";
    return "complete";
  };
  const [uploadOption, setUploadOption] = useState(getInitialUploadOption());
  const [buttonLoading, setButtonLoading] = useState(false);
  const [showErrorReview, setShowErrorReview] = useState(false);
  const [validContainers, setValidContainers] = useState<CreatedContainer[]>([]);

  const params = useParams<{ scheduleId: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const dropDownRef = useRef<(HTMLDivElement | null)[]>([]);
  const gridRef = useRef<SlickGrid1Ref<ImportGridRow> | null>(null);
  const estimateValidateRef = useRef<SlickGrid1Ref<ImportGridRow> | null>(null);
  const validateGridRef = useRef<SlickGrid1Ref<ImportGridRow> | null>(null);
  const dayNames = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

  // `getRequiredFields` returns undefined for a `source` outside the five known ones, which every
  // read below would then throw on; preserved as an erased assertion.
  const requiredFields: ExcelRequiredField[] = getRequiredFields({
    source,
    extraFields: customFields,
    projectLevelFields,
  })!;
  /**
   * The same list in its descriptor form, for the files/assets sources whose readers below need the
   * `headerText` / `value` / `fieldType` members. Filtering is what proves the arm — the schedule,
   * estimate and cost-database sources get plain header strings out of `getRequiredFields`.
   */
  const expectedFields = requiredFields.filter(isExpectedField);
  const exampleFields = getExampleFields({ source, projectLevelFields });

  async function fetchPortalPurchaseUnits() {
    const response = await getPurchaseUnits(null);
    setPurchaseUnitsList(response.data.result ?? []);
  }

  async function fetchCurrentFormTemplate() {
    if (currentData) {
      const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
        projectId: currentData.projectId,
        hierarchyId: currentData.hierarchyId,
        resourceType: formCategories.files,
      });
      if (response.data.status && response.data.data) {
        const templateDoc = response.data.data;
        // SCHEMA GAP: `getFormTemplate` projects `name: 1` off the Template document
        // (project.mongo.service.ts), but `formTemplateDataSchema` does not declare it — so the name
        // is probed through a widened view of the same document rather than asserted into existence.
        const named: { _id: unknown; name?: unknown } = templateDoc;
        setCustomFields([
          {
            _id: templateDoc._id,
            name: typeof named.name === "string" ? named.name : "",
            template: templateDoc.template,
          },
        ]);
      }
    }
  }

  async function fetchProjectLevelFields() {
    if (source === excelImportSources.ASSETS && sourceId) {
      const response = await axiosWrapper.post<GetFormTemplateResponse>(`${getBackendUrl()}/projects/getFormTemplate`, {
        projectId: sourceId,
        resourceType: formCategories.assets,
      });
      if (response.data.status && response.data.data?.template) {
        setProjectLevelFields(response.data.data.template);
      }
    }
  }

  const iconFormatter: ImportGridFormatter = (row, cell, value, columnDef, dataContext) => {
    if (!dataContext.type) return "";
    const container = document.createElement("div");
    createRoot(container).render(
      <IconComponent
        name="details"
        width="1.5rem"
        height="1.5rem"
        position="right"
        className="cursor-pointer"
        // The row's `type` carries the backend's per-row message; it is data, not a translation key.
        toolTipContent={untranslated(String(dataContext.type))}
      />,
    );
    return container;
  };

  const idFormatter: ImportGridFormatter = (row, cell, value, columnDef, dataContext) => {
    if (dataContext.WBSCode) {
      return dataContext.WBSCode;
    }
    return dataContext.customTaskId || "";
  };

  const createFormatterContainer = (styles: Partial<CSSStyleDeclaration> = {}): HTMLDivElement => {
    const container = document.createElement("div");
    container.style.overflow = "hidden";
    Object.assign(container.style, styles);
    return container;
  };

  const cellContainersRef = useRef<Map<string, HTMLDivElement>>(new Map());
  const getCellContainer = (
    row: number,
    columnKey: string | number,
    styles: Partial<CSSStyleDeclaration> = {},
  ): HTMLDivElement => {
    const containers = cellContainersRef.current;
    const key = `${row}:${columnKey}`;
    const existing = containers.get(key);
    if (existing) return existing;
    if (containers.size > 300) {
      for (const [mapKey, element] of containers) {
        if (!element.isConnected) containers.delete(mapKey);
      }
    }
    const container = createFormatterContainer(styles);
    containers.set(key, container);
    return container;
  };

  const errorFormatter: ImportGridFormatter = (_row, _cell, _value, _columnDef, dataContext) => {
    if (!Array.isArray(dataContext.errors) || dataContext.errors.length === 0) return "";
    const errorText = dataContext.errors.join(", ");
    const container = getCellContainer(_row, _columnDef.id, {
      display: "flex",
      alignItems: "center",
      width: "100%",
    });
    container.title = errorText;
    createRoot(container).render(
      <div className="flex items-center gap-2 min-w-0 overflow-hidden w-full">
        <div className="shrink-0 flex items-center">
          <IconComponent name="DetailsRed" width="1rem" height="1rem" />
        </div>
        <span className="text-[#E05650] truncate">{errorText}</span>
      </div>,
    );
    return container;
  };

  const textWithTooltipFormatter: ImportGridFormatter = (_row, _cell, value, _columnDef, dataContext) => {
    const displayText = String(value ?? dataContext[_columnDef.field] ?? "");
    if (!displayText) return "-";
    const container = getCellContainer(_row, _columnDef.id);
    createRoot(container).render(
      <ToolTipComponent position="bottom" content={untranslated(displayText)}>
        <span className="truncate block">{displayText}</span>
      </ToolTipComponent>,
    );
    return container;
  };

  const headerFormatter =
    (column: ExcelGridColumn): ImportGridFormatter =>
    (_row, _cell, _value, _columnDef, _dataContext) => {
      if (column.isMandatory) {
        const container = document.createElement("div");
        createRoot(container).render(
          <div className="flex">
            <span className="text-2xl leading-4 font-light text-red-500">*</span>
            <p>{column.headerText || column.name}</p>
          </div>,
        );
        return container;
      }
      return column.headerText || column.name || "";
    };

  const getColumnDefinitions = ({
    columnNames,
    allowCheckboxSelection,
    isValidateState,
    source: colSource,
    dateTimeFields,
    dateTimeFieldsMap,
  }: {
    columnNames?: ExcelRequiredField[] | null;
    allowCheckboxSelection?: boolean;
    isValidateState?: boolean;
    source?: string;
    dateTimeFields?: string[];
    dateTimeFieldsMap?: Record<string, string | undefined>;
  }): ImportGridColumn[] => {
    const syncfusionColumns = getGridColumns({
      columnNames,
      allowCheckboxSelection,
      isValidateState,
      source: colSource!,
      iconTemplate: iconFormatter,
      errorTemplate: null,
      idTemplate: null,
    });

    return syncfusionColumns
      .filter((col) => col.type !== "checkbox")
      .map((col, index) => {
        const slickColumn: ImportGridColumn = {
          id: col.field || `col_${index}`,
          field: col.field || "",
          name: col.headerText || col.field || "",
          minWidth: parseInt(col.width!) || 120,
        };

        if (col.template) {
          if (col.width === "40") {
            slickColumn.formatter = iconFormatter;
          }
        }

        if (col.field === "errors") {
          slickColumn.formatter = errorFormatter;
          slickColumn._valueGetter = (row) => (Array.isArray(row.errors) ? row.errors.join(", ") : "");
          slickColumn.exportCustomFormatter = (_row, _cell, _value, _columnDef, dataContext) =>
            Array.isArray(dataContext.errors) ? dataContext.errors.join(", ") : "";
        }

        if (col.isPrimaryKey && colSource === excelImportSources.SCHEDULE) {
          slickColumn.formatter = idFormatter;
        }

        if (col.isMandatory) {
          slickColumn.headerFormatter = headerFormatter(col);
        }

        // `col.field` is optional on a column descriptor; checking it (rather than widening the
        // array with an assertion) keeps `includes(undefined) === false` as the intent.
        const isDateTimeField = col.type === "date" || (col.field !== undefined && dateTimeFields?.includes(col.field));
        if (isDateTimeField) {
          const displayAs = col.displayAs ?? dateTimeFieldsMap?.[col.field!];
          const dateExportGetter = (row: ImportGridRow) => formatDateFieldValue(row[col.field!], displayAs);
          slickColumn._valueGetter = dateExportGetter;
          slickColumn.exportCustomFormatter = (_row, _cell, _value, _columnDef, dataContext) =>
            dateExportGetter(dataContext);
          slickColumn.formatter = (_row, _cell, value, columnDef, dataContext) => {
            const rawVal = value ?? dataContext[columnDef.field];
            const displayText = formatDateFieldValue(rawVal, displayAs);
            if (!displayText) return "-";
            const container = getCellContainer(_row, columnDef.id);
            createRoot(container).render(
              <ToolTipComponent position="bottom" content={untranslated(displayText)}>
                <span>{displayText}</span>
              </ToolTipComponent>,
            );
            return container;
          };
        }

        if (!slickColumn.formatter) {
          slickColumn.formatter = textWithTooltipFormatter;
        }

        if (!slickColumn._valueGetter) {
          slickColumn._valueGetter = (row) => {
            const val = row[col.field!];
            if (Array.isArray(val)) return val.join(", ");
            return val ?? "";
          };
        }

        return slickColumn;
      });
  };

  const handleRowSelectionChanged = useCallback(
    async (selectedRowIds: (string | number)[], dataView: SlickGrid1Ref<ImportGridRow>["dataView"]) => {
      if (selectedRowIds.length === 1) {
        const selectedRecord = gridItemById(dataView, selectedRowIds[0]!);
        if (selectedRecord) {
          const selectedRow = Object.entries(selectedRecord)
            .filter(([key]) => key !== "id")
            .map(([, val]) => (typeof val === "string" ? val.trim() : String(val ?? "").trim()))
            .filter((val) => val !== "");
          setAllColumnNames(selectedRow);
          const selectedIndex = dataView.getIdxById(selectedRowIds[0]!);
          setHeaderIndex(selectedIndex!);
          setSelectedHeaderRowId(String(selectedRowIds[0]));
          const temp = allJsonData!.filter((data, index) => index > selectedIndex!);
          const convertArrayDataToObject = (data: string[], colName: string[]): ExcelRow => {
            const resultObject: ExcelRow = {};
            colName.forEach((value, index) => {
              resultObject[value] = data[index]!;
            });
            return resultObject;
          };
          const mappedData = temp.map((data) => convertArrayDataToObject(data, selectedRow));
          if (source == "assets") {
            const templateNames = new Set<string>();
            if (Array.isArray(mappedData) && mappedData.length) {
              mappedData.forEach(
                (item) => item.Template?.length && templateNames.add(item.Template.trim().toLowerCase()),
              );
            }
            const formsList = await getTemplates({ value: { resourceType: formCategories.assets } });
            // SCHEMA GAP: `getTemplatesResponse.result` is `z.array(z.unknown())`, so nothing about a
            // template doc is known here — each row is checked for the three members this reads
            // instead of the whole list being named `ExcelFormTemplate[]`.
            const formRows: unknown[] = formsList.data.result ?? [];
            if (formRows.length) {
              const templates = formRows
                .filter(isRecord)
                .filter((form) => typeof form.name === "string" && templateNames.has(form.name.toLowerCase()))
                .map((form) => ({
                  template: Array.isArray(form.template) ? form.template : undefined,
                  _id: String(form._id),
                  name: String(form.name).toLowerCase(),
                }));
              setCustomFields(templates);
            }
          }

          setFilteredJsonData(mappedData);
          setEnableNextButton(true);
        }
      } else {
        setEnableNextButton(false);
        setFilteredJsonData(null);
        setAllColumnNames([]);
        setHeaderIndex(null);
        setSelectedHeaderRowId(null);
      }
    },
    [allJsonData, source],
  );
  const headerSelectionDataset = useMemo(
    () => (allJsonData ?? []).map((row, index) => ({ ...row, id: `row_${index}` })),
    [allJsonData],
  );

  const gridColumnDefinitions = useMemo(
    () => (props: ImportGridProps) =>
      getColumnDefinitions({
        columnNames: props.columnNames,
        allowCheckboxSelection: true,
        isValidateState: props.isValidateState,
        source: props.source,
        dateTimeFields: props.dateTimeFields,
        dateTimeFieldsMap: props.dateTimeFieldsMap,
      }),
    // getColumnDefinitions is a new ref each render but state-independent — adding it would rebuild the grid; safe to omit.

    [],
  );

  const scheduleTreeColumnDefinitions = useMemo(
    () =>
      getColumnDefinitions({
        columnNames: null,
        allowCheckboxSelection: false,
        isValidateState: false,
        source,
      }),
    // getColumnDefinitions is a new ref each render but state-independent — adding it would rebuild the grid; safe to omit.

    [source],
  );

  const scheduleValidateColumnDefinitions = useMemo(
    () =>
      getColumnDefinitions({
        columnNames: null,
        allowCheckboxSelection: false,
        isValidateState: true,
        source,
      }),
    // getColumnDefinitions is a new ref each render but state-independent — adding it would rebuild the grid; safe to omit.

    [source],
  );

  const estimateTreeColumnDefinitions = useMemo(
    () =>
      getColumnDefinitions({
        columnNames: null,
        allowCheckboxSelection: false,
        isValidateState: false,
        source,
      }),
    // getColumnDefinitions is a new ref each render but state-independent — adding it would rebuild the grid; safe to omit.

    [source],
  );

  const estimateValidateColumnDefinitions = useMemo(
    () =>
      getColumnDefinitions({
        columnNames: null,
        allowCheckboxSelection: false,
        isValidateState: true,
        source,
      }),
    // getColumnDefinitions is a new ref each render but state-independent — adding it would rebuild the grid; safe to omit.

    [source],
  );

  const assetsColumnDefinitions = useMemo(
    () =>
      getColumnDefinitions({
        columnNames: requiredFields,
        allowCheckboxSelection: false,
        isValidateState: true,
        source,
      }),

    [customFields, projectLevelFields],
  );
  const restoreHeaderSelection = useCallback(
    (gridRef: SlickGrid1Ref<ImportGridRow>) => {
      if (selectedHeaderRowId === null) return;
      const row = gridRef.dataView.getRowById(selectedHeaderRowId);
      if (typeof row !== "number" || row < 0) return;
      gridRef.slickGrid.setSelectedRows([row]);
    },
    [selectedHeaderRowId],
  );

  const getGridComponent = useCallback(
    (props: ImportGridProps) => (
      <SlickGrid1
        key={props.rowSelected ? "row-select" : "no-select"}
        ref={gridRef}
        dataset={toGridRows(props.dataSource)}
        // The columns are built by hand (their `field` keys are the sheet's, known only at runtime),
        // so they go through SlickGrid1's own brander rather than being asserted at this boundary.
        columnDefinitions={withDynamicColumns<ImportGridRow>(gridColumnDefinitions(props))}
        gridOptions={{ rowHeight: 34, ...(props.height !== undefined && { gridHeight: props.height }) }}
        disableCheckBoxSelection={!props.rowSelected}
        excelExport={props.allowExport}
        onSelectedRowIdsChanged={
          props.rowSelected
            ? (args) => {
                // SlickGrid1 types this callback's argument as an opaque bag; the selection payload
                // it really carries is named here.
                const selectedRowIds =
                  (args as typeof args & { detail?: { args?: { selectedRowIds?: (string | number)[] } } }).detail?.args
                    ?.selectedRowIds ?? [];
                const dataView = gridRef.current?.dataView;
                if (dataView) {
                  handleRowSelectionChanged(selectedRowIds, dataView);
                }
              }
            : undefined
        }
      />
    ),
    [gridColumnDefinitions, handleRowSelectionChanged, restoreHeaderSelection],
  );

  const scheduleFinalTreeGrid = useCallback(
    (props: ImportGridProps) => (
      <SlickGrid1
        dataset={toGridRows(props.dataSource)}
        columnDefinitions={withDynamicColumns<ImportGridRow>(scheduleTreeColumnDefinitions)}
        idMapping="mysqlId"
        parentIdMapping="parentId"
        gridOptions={{ rowHeight: 34 }}
      />
    ),
    [scheduleTreeColumnDefinitions],
  );

  const scheduleValidateGrid = useCallback(
    (props: ImportGridProps) => (
      <SlickGrid1
        ref={validateGridRef}
        dataset={toGridRows(props.dataSource)}
        columnDefinitions={withDynamicColumns<ImportGridRow>(scheduleValidateColumnDefinitions)}
        gridOptions={{ rowHeight: 34 }}
      />
    ),
    [scheduleValidateColumnDefinitions],
  );

  const estimateFinalTreeGrid = useCallback(
    (props: ImportGridProps) => (
      <SlickGrid1
        dataset={toGridRows(props.dataSource)}
        columnDefinitions={withDynamicColumns<ImportGridRow>(estimateTreeColumnDefinitions)}
        idMapping="rowNo"
        parentIdMapping="parent"
        gridOptions={{ rowHeight: 34 }}
      />
    ),
    [estimateTreeColumnDefinitions],
  );

  const estimateValidateGrid = useCallback(
    (props: ImportGridProps) => (
      <SlickGrid1
        ref={estimateValidateRef}
        dataset={toGridRows(props.dataSource)}
        columnDefinitions={withDynamicColumns<ImportGridRow>(estimateValidateColumnDefinitions)}
        gridOptions={{ rowHeight: 34 }}
      />
    ),
    [estimateValidateColumnDefinitions],
  );

  const handleDropdownChange = (newValue: ExcelMatchOption | null, columnName: string) => {
    const selectedValue = newValue?.value.trim() || "";
    // A field may only be mapped from one sheet column, so the column that already holds this value
    // loses it. Written as a lookup rather than a flag flipped inside a callback: the last match is
    // the one the previous loop kept, and a `let` assigned from a callback also defeats narrowing.
    let mappedElsewhere: string | undefined;
    Object.keys(columnMapper).forEach((key) => {
      if (selectedValue === columnMapper[key]) {
        mappedElsewhere = key;
      }
    });
    const tempObj: ExcelColumnMapper = {
      ...columnMapper,
      [columnName]: selectedValue,
    };
    if (mappedElsewhere !== undefined) {
      delete tempObj[mappedElsewhere];
    }
    setColumnMapper(tempObj);
  };

  const getFieldName = (value: string) => {
    const options =
      getMatchDropdownDataSource({
        source,
        customFields,
        projectLevelFields,
      }) || [];
    const matchedOption = options.find((option) => option.value === value);
    return matchedOption?.name || value;
  };

  const autoMatching = async () => {
    let temp: ExcelColumnMapper = {};
    if (source == excelImportSources.SCHEDULE) {
      allColumnNames.map((column) => {
        if (requiredFields.includes(column)) {
          if (!excludeAutoMatchingColumns.includes(column)) {
            temp = {
              ...temp,
              [column]: column,
            };
          }
        }
      });
      setColumnMapper(temp);
    } else if (source == excelImportSources.FILES) {
      allColumnNames.map((column) => {
        const matchedField = expectedFields.find((field) => field.headerText == column);
        if (matchedField) {
          temp = {
            ...temp,
            [column]: matchedField.value || column,
          };
        }
      });
      setColumnMapper(temp);
    } else if (source == excelImportSources.ASSETS) {
      allColumnNames.map((column) => {
        const matchedField = expectedFields.find((field) => field.headerText.toLowerCase() == column.toLowerCase());
        if (matchedField) {
          temp = {
            ...temp,
            [column]:
              column == "Asset Tag"
                ? "assetTag"
                : column == "Asset Name"
                  ? "assetName"
                  : matchedField.value || column.toLowerCase(),
          };
        }
      });
      setColumnMapper(temp);
    } else {
      allColumnNames.map((column) => {
        if (requiredFields.includes(column)) {
          temp = {
            ...temp,
            [column]: column == "HSN/SAC" ? "HSN_SAC" : column == "Purchase Unit Name" ? "unit" : column.toLowerCase(),
          };
        }
      });
      setColumnMapper(temp);
    }
  };

  const validatorFunction = async () => {
    if (source === excelImportSources.SCHEDULE) {
      validateScheduleData({
        sourceId: params.scheduleId,
        // Set by the Select Header step, which is the only route to this validator.
        filteredJsonData: filteredJsonData!,
        notValidPredecessors,
        setEnableNextButton,
        setScheduleIcorrectData,
        setIsScheduleDataValid,
        setButtonLoading,
        setFinalData,
        columnMapper,
        startDateTypeFormat,
        finishDateTypeFormat,
        wbsPathTypeIndex,
      });
    } else if (source === excelImportSources.ESTIMATE || source === excelImportSources.COST_DATABASE) {
      validateEstimateData({
        source,
        sourceId,
        estimateData,
        headerIndex,
        filteredJsonData: filteredJsonData!,
        purchaseUnitsList,
        setEstimateIcorrectData,
        setButtonLoading,
        setIsEstimateDataValid,
        setEnableNextButton,
        setFinalData,
        columnMapper,
        dispatch,
      });
    } else if (source === excelImportSources.FILES) {
      await saveContainers({
        filteredJsonData: filteredJsonData!,
        setFinalData,
        setButtonLoading,
        columnMapper,
        extraFields: customFields,
        setEnableNextButton,
        currentData,
        matchByDocumentNumber,
      });
    } else if (source === excelImportSources.ASSETS) {
      await validateAssetData({
        sourceId,
        filteredJsonData: filteredJsonData!,
        setFinalData,
        setEnableNextButton,
        setButtonLoading,
        extraFields: customFields,
        projectLevelFields,
        columnMapper,
      });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFile(file);
      setSelectedHeaderRowId(null);
      if (file.name.split(".").pop() == "xml") {
        setIsXml(true);
      } else {
        setIsXml(false);
      }
      setEnableNextButton(true);
    } else {
      setEnableNextButton(false);
    }
  };

  const showValidateData = () => {
    if (source == excelImportSources.FILES) {
      const dateTimeFieldsMap: Record<string, string | undefined> = {};
      expectedFields
        .filter((f) => f.fieldType === "date-time")
        .forEach((f) => {
          dateTimeFieldsMap[f.headerText] = f.displayAs;
        });
      return getGridComponent({
        dataSource: finalData,
        columnNames: allColumnNames,
        isValidateState: true,
        source,
        dateTimeFields: Object.keys(dateTimeFieldsMap),
        dateTimeFieldsMap,
        allowExport: true,
      });
    }
    if (source == excelImportSources.SCHEDULE) {
      if (isScheduleDataValid || isXml) {
        return scheduleFinalTreeGrid({ dataSource: finalData });
      }
      const allData: ExcelImportRow[] = [];
      finalData.map((data) => {
        // The rows the validator rejected replace the imported row; a hand-rolled "found" flag
        // flipped inside the callback is invisible to control flow, which made `!isFound` read as
        // statically true.
        const rowErrors = scheduleIcorrectData.filter((error) => error.id == data.customTaskId);
        allData.push(...rowErrors);
        if (rowErrors.length === 0) {
          data.startDate = data.startDate
            ? typeof data.startDate == "string"
              ? data.startDate
              : new Date(toDateInput(data.startDate))
            : null;
          data.endDate = data.endDate
            ? typeof data.endDate == "string"
              ? data.endDate
              : new Date(toDateInput(data.endDate))
            : null;
          allData.push(data);
        }
      });
      const currentDataSource = showOnlyErrors ? scheduleIcorrectData : allData;
      return (
        <div>
          <div className="flex justify-between">
            <div className="mb-2 font-bold text-sm">{t("Following Rows have Errors.")}</div>
            <ToggleSwitchComponent
              label="Only Show Rows with error "
              checked={showOnlyErrors}
              onChange={() => setShowOnlyErrors(!showOnlyErrors)}
            />
          </div>
          {scheduleValidateGrid({ dataSource: currentDataSource })}
        </div>
      );
    }
    if (source == excelImportSources.ASSETS) {
      return (
        <SlickGrid1
          dataset={finalData}
          columnDefinitions={withDynamicColumns<ImportGridRow>(assetsColumnDefinitions)}
          gridOptions={{ rowHeight: 34 }}
        />
      );
    }
    if (isEstimateDataValid) {
      // had to do because in treeComponent "level" is reserved key and parentmapping should start with 1 either it didn't show all rows.
      const newData = finalData.map((item) => {
        const newItem: ExcelImportRow = { ...item };
        delete newItem.level;

        // `Number()` rather than an assertion: a sheet cell is `unknown` here, and `-` already
        // coerced it exactly this way.
        newItem.rowNo = Number(newItem.rowNo) - 1;
        if (newItem.parent != "") {
          newItem.parent = Number(newItem.parent) - 1;
        } else {
          newItem.parent = null;
        }
        return newItem;
      });
      return estimateFinalTreeGrid({ dataSource: newData });
    }
    const allData: ExcelImportRow[] = [];
    finalData.map((data) => {
      // `some` rather than a flag flipped inside a callback: control flow cannot see the latter, so
      // the `!isFound` it fed read as statically true.
      const isFound = estimateIcorrectData.some((error) => error.row == data.rowNo);
      if (!isFound) {
        allData.push(data);
      }
    });
    allData.push(...estimateIcorrectData);
    const currentDataSource = showOnlyErrors ? estimateIcorrectData : allData;
    return (
      <div className="flex flex-col h-full">
        <div className="flex justify-between pe-2">
          <div className="mb-2 font-bold text-sm">{t("Following Rows have Errors.")}</div>
          <ToggleSwitchComponent
            label="Only Show Rows with error "
            checked={showOnlyErrors}
            onChange={() => setShowOnlyErrors(!showOnlyErrors)}
          />
        </div>
        <div className="flex h-full overflow-auto">{estimateValidateGrid({ dataSource: currentDataSource })}</div>
      </div>
    );
  };

  useEffect(() => {
    if (currentTab == "Match Columns") {
      allColumnNames.map((col, index) => {
        const temp = Object.keys(columnMapper);
        if (!temp.includes(col)) {
          const dropdownElement = dropDownRef.current[index];
          if (dropdownElement) {
            // KNOWN BUG, preserved verbatim: the AutoComplete ref is the wrapper element, which has
            // no `value`, so this reset never reaches the input. `Object.assign` writes the same
            // property without claiming the element declares one.
            Object.assign(dropdownElement, { value: null });
          }
        }
      });
    }

    if (currentTab == "Validate Data" && isXml) {
      setEnableNextButton(true);
    }
  }, [columnMapper, currentTab]);

  useEffect(() => {
    fetchPortalPurchaseUnits();
    fetchCurrentFormTemplate();
    fetchProjectLevelFields();
  }, []);

  useEffect(() => {
    if (
      source == excelImportSources.SCHEDULE &&
      wbsPathTypeIndex != null &&
      startDateTypeFormat != null &&
      finishDateTypeFormat != null &&
      Object.keys(columnMapper).length == scheduleDropDownDataSource.length
    ) {
      setEnableNextButton(true);
    } else if (source === excelImportSources.FILES) {
      // Enable when every mandatory expected field has a mapped column. Optional columns
      // (e.g. Folder Name, Revision) shouldn't block — a strict count match breaks the
      // moment any optional column is missing or has a header mismatch.
      const mappedValues = new Set(Object.values(columnMapper));
      const mandatoryMapped = expectedFields
        .filter((f) => f.isMandatory)
        .every((f) => mappedValues.has(f.value || f.headerText));
      setEnableNextButton(mandatoryMapped);
    } else if (
      (source == excelImportSources.ESTIMATE || source === excelImportSources.COST_DATABASE) &&
      Object.keys(columnMapper).length == requiredFields.length
    ) {
      setEnableNextButton(true);
    } else if (source == excelImportSources.ASSETS) {
      const allMapped =
        allColumnNames.length > 0 && allColumnNames.every((col) => columnMapper[col] && columnMapper[col] !== "");
      setEnableNextButton(allMapped);
    }
  }, [columnMapper, wbsPathTypeIndex, startDateTypeFormat, finishDateTypeFormat]);

  const getHeader = (headerName: string, index: number) => (
    <div className={currentTab == headerName ? "flex text-sm gap-1" : "flex opacity-20 text-sm gap-1"}>
      <span className="font-semibold h-5 w-5 flex justify-center items-center rounded-full bg-button_light">
        {index}
      </span>
      {headerName}
    </div>
  );

  const handleXmlSoftwareSelection: NonNullable<ComponentProps<typeof SelectDropDown>["select"]> = (args) => {
    setXmlSoftware(args.item.properties.text!);
  };

  // These datasources key their entries by array POSITION (`id: index`), so the menu item's id is
  // read back as that position — `calendarArray` is null until an XML upload has been parsed, which
  // is what the lookup below returns `undefined` for.
  const handleCalendarSelection: NonNullable<ComponentProps<typeof SelectDropDown>["select"]> = (args) => {
    const tempWorkTimingsArray: { from: number; to: number }[][] = [];
    const workTimingsTempdataSource: XmlDropdownItem[] = [];
    const tempdataSourceArray: string[] = [];
    const tempWorkingDays: string[] = [];
    const tempHolidays: unknown[] = [];
    let counter = 0;

    const selectedCalendarEntry = calendarArray?.[Number(args.item.properties.id)];
    if (!selectedCalendarEntry) return;

    if (xmlSoftware == "P6") {
      selectedCalendarEntry.HolidayOrExceptions!.HolidayOrException.map((day) => {
        if (day.WorkTime["@_xsi:nil"]) {
          tempHolidays.push({ FromDate: day.Date, ToDate: day.Date });
        }
      });
      setHolidays(tempHolidays);

      selectedCalendarEntry.StandardWorkWeek!.StandardWorkHours.forEach((day) => {
        if (!day.WorkTime["@_xsi:nil"]) {
          tempWorkingDays.push(day.DayOfWeek!);
          const worktimings: { from: number; to: number }[] = [];
          let tempdatasource = "";
          if (!day.WorkTime.length) {
            const fromHour = parseInt(day.WorkTime.Start!.split(":")[0]!, 10);
            let toHour = parseInt(day.WorkTime.Finish!.split(":")[0]!, 10);
            const finishMinutes = parseInt(day.WorkTime.Finish!.split(":")[1]!, 10);
            if (finishMinutes !== 0) {
              toHour += 1;
            }
            worktimings.push({ from: fromHour, to: toHour });
            tempdatasource += `${fromHour}-${toHour}`;
          } else {
            day.WorkTime.forEach((range, index) => {
              const fromHour = parseInt(range.Start!.split(":")[0]!, 10);
              let toHour = parseInt(range.Finish!.split(":")[0]!, 10);
              const finishMinutes = parseInt(range.Finish!.split(":")[1]!, 10);
              if (finishMinutes !== 0) {
                toHour += 1;
              }
              worktimings.push({ from: fromHour, to: toHour });
              tempdatasource += `${fromHour}-${toHour}`;
              if (index < day.WorkTime.length - 1) {
                tempdatasource += " , ";
              }
            });
          }

          if (!tempdataSourceArray.includes(tempdatasource)) {
            tempdataSourceArray.push(tempdatasource);
            tempWorkTimingsArray.push(worktimings);
            workTimingsTempdataSource.push({
              text: tempdatasource,
              id: String(counter),
            });
            counter += 1;
          }
        }
      });
    } else if (xmlSoftware == "MSP") {
      const tempWorkTiming: XmlWorkTimeValue[] = [];

      selectedCalendarEntry.WeekDays!.WeekDay.map((day) => {
        if (day.DayType == 0 && day.DayWorking == 0) {
          tempHolidays.push(day.TimePeriod);
        } else if (day.DayType > 0 && day.DayWorking == 1) {
          tempWorkTiming.push(day.WorkingTimes.WorkingTime);
          tempWorkingDays.push(dayNames[day.DayType % 7]!);
        }
      });

      tempWorkTiming.map((day) => {
        const worktimings: { from: number; to: number }[] = [];
        let tempdatasource = "";
        if (!day.length) {
          const fromHour = parseInt(day.FromTime!.split(":")[0]!, 10);
          let toHour = parseInt(day.ToTime!.split(":")[0]!, 10);
          const finishMinutes = parseInt(day.ToTime!.split(":")[1]!, 10);
          if (finishMinutes !== 0) {
            toHour += 1;
          }
          worktimings.push({ from: fromHour, to: toHour });
          tempdatasource += `${fromHour}-${toHour}`;
        } else {
          day.forEach((range, index) => {
            const fromHour = parseInt(range.FromTime!.split(":")[0]!, 10);
            let toHour = parseInt(range.ToTime!.split(":")[0]!, 10);
            const finishMinutes = parseInt(range.ToTime!.split(":")[1]!, 10);
            if (finishMinutes !== 0) {
              toHour += 1;
            }
            worktimings.push({ from: fromHour, to: toHour });
            tempdatasource += `${fromHour}-${toHour}`;
            if (index < day.length - 1) {
              tempdatasource += " , ";
            }
          });
        }
        if (!tempdataSourceArray.includes(tempdatasource)) {
          tempdataSourceArray.push(tempdatasource);
          tempWorkTimingsArray.push(worktimings);
          workTimingsTempdataSource.push({ text: tempdatasource, id: String(counter) });
          counter += 1;
        }
      });
      setHolidays(tempHolidays);
    }
    setTimingsDropdownDatasource(workTimingsTempdataSource);
    setWorkWeek(tempWorkingDays);
    setSelectedCalendar(args.item.properties.text!);
    setWorkTimingsArray(tempWorkTimingsArray);
    setWorkTimingsForDropdown("");
  };

  const handleTimingsSelection: NonNullable<ComponentProps<typeof SelectDropDown>["select"]> = (args) => {
    // Same positional ids as the calendar menu above: the item's id IS the index into both arrays.
    const timingsIndex = Number(args.item.properties.id);
    const selectedTimings = workTimingsArray[timingsIndex];
    const selectedEntry = timingsDropdownDatasource[timingsIndex];
    if (!selectedTimings || !selectedEntry) return;
    setWorkTimings(selectedTimings);
    setWorkTimingsForDropdown(selectedEntry.text);
    setEnableNextButton(true);
  };

  const getSizeForColumn = () => (source === excelImportSources.COST_DATABASE ? 225 : 160);
  const getHeaderLine = () => <div className="border-black border-b-2 w-10 opacity-20" />;

  const importModalContent = () => {
    if (currentTab == "Upload File") {
      const fieldsToShow = requiredFields;
      const dataForFields = exampleFields;
      return (
        <div className="h-full p-6">
          <div className="">
            <div className="font-bold text-xl"> {t("Upload File")} </div>
            <div className="mt-4 font-bold text-lg"> {t("Data that we expect:")} </div>
            <div className="text-sm"> {t("(You will have a chance to rename or remove columns in next steps)")}</div>
          </div>
          <div className="mt-6" style={{ height: "9.375rem" }}>
            {getGridComponent({
              dataSource: dataForFields,
              columnNames: fieldsToShow,
              source,
            })}
          </div>

          <div className=" mt-6 font-semibold text-sm">
            {t("Please")}{" "}
            <a
              href="#"
              className="hover:underline mx-1 cursor-pointer text-blue-400"
              onClick={(e) => {
                e.preventDefault();
                getSampleFile({ source, extraFields: customFields, projectLevelFields });
              }}
            >
              {t("download")}
            </a>{" "}
            {t("the template first and use the exact format when you upload in case of .XLS/.XLSX/.CSV files")}
          </div>

          <div className="mt-8 me-6 border-2 border-solid border-gray-200 w-full p-2">
            <label htmlFor="fileUploadExcelImport">
              <input
                type="file"
                accept=".xls,.xlsx,.csv,.xml"
                id="fileUploadExcelImport"
                className="hidden"
                onChange={handleFileUpload}
              />
              <div className="flex relative cursor-pointer">
                <div
                  className={`px-2 py-1 shadow-md rounded ${file ? "opacity-40" : ""}`}
                  style={{ backgroundColor: "#ecf3fe" }}
                >
                  {t("Upload File (.xlsx, .xls, .csv or .xml supported)")}
                </div>
                <span className="justify-center items-center ms-2 mt-1 text-sm">
                  {" "}
                  {file ? file.name : t("No File Chosen")}{" "}
                </span>
              </div>
            </label>
          </div>
          <div className="ms-4 text-sm">
            {t("**only imports data from the first sheet if your Excel file contains multiple sheets.")}
          </div>
          {isXml && (
            <div className=" flex items-center gap-6 mt-8 font-semibold text-sm">
              {t("Choose the software from which XML is exported")}
              <SelectDropDown
                cssClass="bg-primary_blue h-8 px-4 rounded-lg"
                items={xmlDropdownDatasource}
                select={handleXmlSoftwareSelection}
                content={xmlSoftware}
              />
            </div>
          )}
        </div>
      );
    }
    if (currentTab == "Select Header") {
      return (
        <div className="flex flex-col h-full p-6">
          <div className="font-bold text-xl h-6">{t("Select Header Row")}</div>
          <div className="mt-4 h-full">
            {getGridComponent({
              dataSource: headerSelectionDataset,
              columnNames: Object.keys(allJsonData![0]!).filter((key) => key !== "id"),
              rowSelected: true,
            })}
          </div>
        </div>
      );
    }
    if (currentTab == "Match Columns") {
      return (
        <div className="p-6 h-full w-full flex flex-col">
          <div className="font-bold text-xl">{t("Match Columns")}</div>
          <div className="mt-4 font-bold text-md"> {t("Your Table")}</div>
          <div className="mt-4 h-full">
            {getGridComponent({
              dataSource: filteredJsonData,
              columnNames: allColumnNames,
              height: 100,
            })}
          </div>
          <div className="mt-8 font-bold text-md ">{t("Will Become")}</div>
          <div>
            <div className="flex w-full gap-3 py-5 overflow-x-auto">
              {allColumnNames.map((columnName, index) => (
                <div key={index} className="flex-1 flex-col">
                  <ToolTipComponent content={untranslated(getFieldName(columnMapper[columnName]!))}>
                    <AutoComplete
                      key={index}
                      id={`${index}`}
                      // RESIDUAL implicit-any, and the `any` is load-bearing. Annotating `ref` makes
                      // this arrow's return type concrete (`HTMLDivElement | null`, the value of the
                      // assignment), and React 19 types a ref callback's return as
                      // `void | (() => VoidOrUndefinedOnly)` — a union, so TS's "return value is
                      // ignored in a void position" rule does not apply and the assignment is
                      // rejected. Left implicit because `ref: any` makes the assignment `any`, which
                      // the union does accept.
                      //
                      // Every route out changes the emitted JS, so none is available here:
                      //   - a statement body (`{ ... }`) or a `void` operator: new tokens;
                      //   - asserting the whole arrow, `((ref) => ...) as (i: unknown) => void`:
                      //     MEASURED at +2 bytes — esbuild keeps the parentheses around a
                      //     parenthesized arrow in a JSX attribute. (Contrast `new (X as T)(a)`,
                      //     where the parens ARE dropped and the emit is identical — the two look
                      //     alike and behave differently, so each has to be measured.)
                      //
                      // NOTE: returning a non-function from a ref callback is a genuine React 19
                      // bug here, not just a typing gap — React treats a returned function as the
                      // cleanup, and this returns the element.
                      // @ts-expect-error -- TS7006: annotating `ref` is the obvious fix but trades this
                      // for a return-type error: React 19 types a ref callback's return as
                      // `void | (() => VoidOrUndefinedOnly)` -- a union, so TS's "return ignored in a void
                      // position" rule does not apply, and this arrow returns the assigned element. All
                      // three erased routes were MEASURED and each changes emit (see the note above), so
                      // the real fix is a statement body: `ref={(ref) => { dropDownRef.current[index] = ref; }}`.
                      ref={(ref) => (dropDownRef.current[index] = ref)}
                      options={getMatchDropdownDataSource({
                        source,
                        customFields,
                        projectLevelFields,
                      })}
                      valueField="value"
                      getOptionLabel={(option) => option.name || ""}
                      value={columnMapper[columnName]}
                      onChange={(_event, newValue) => {
                        handleDropdownChange(newValue, columnName);
                      }}
                      sx={{ width: `${getSizeForColumn() / 16}rem`, minWidth: "6.25rem" }}
                      renderInput={(params) => <TextFieldComponent {...params} />}
                    />
                  </ToolTipComponent>
                  {columnMapper[columnName] == "WBSPath" && (
                    <div className="mt-6 gap-4">
                      <AutoComplete
                        options={wbsPathTypeDatasource}
                        valueField="index"
                        getOptionLabel={(option) => option.name || ""}
                        // The picked ROW, not its index: `valueField` only resolves a string value
                        // against the options, so handing it the number left the box blank.
                        value={wbsPathTypeDatasource.find((option) => option.index === wbsPathTypeIndex) ?? null}
                        onChange={(_event, newValue) => {
                          setWbsPathTypeIndex(newValue?.index ?? null);
                        }}
                        sx={{ width: `${getSizeForColumn() / 16}rem`, minWidth: "6.25rem" }}
                        renderInput={(params) => <TextFieldComponent {...params} />}
                      />
                    </div>
                  )}
                  {columnMapper[columnName] == "Start" && (
                    <div className="mt-6 gap-4">
                      <AutoComplete
                        options={dateTypeDatasource}
                        valueField="value"
                        getOptionLabel={(option) => option.name || ""}
                        value={startDateTypeFormat}
                        onChange={(_event, newValue) => {
                          setStartDateTypeFormat(newValue?.value ?? null);
                        }}
                        sx={{ width: `${getSizeForColumn() / 16}rem`, minWidth: "6.25rem" }}
                        renderInput={(params) => <TextFieldComponent {...params} />}
                      />
                    </div>
                  )}
                  {columnMapper[columnName] == "Finish" && (
                    <div className="mt-6 gap-4">
                      <AutoComplete
                        options={dateTypeDatasource}
                        valueField="value"
                        getOptionLabel={(option) => option.name || ""}
                        value={finishDateTypeFormat}
                        onChange={(_event, newValue) => {
                          setFinishDateTypeFormat(newValue?.value ?? null);
                        }}
                        sx={{ width: `${getSizeForColumn() / 16}rem`, minWidth: "6.25rem" }}
                        renderInput={(params) => <TextFieldComponent {...params} />}
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    }
    if (currentTab == "Validate Data") {
      return (
        <div className="flex flex-col h-full overflow-hidden p-6 w-full">
          <div className="flex justify-between">
            <div className="font-bold text-xl mb-3 h-6">{t("Validate Data")}</div>
          </div>
          <div className="flex-1 overflow-auto">{showValidateData()}</div>
          {isXml && (
            <div className=" flex items-center gap-4 font-semibold text-sm mt-4">
              {t("Select the calendar")}
              <SelectDropDown
                cssClass="bg-primary_blue px-4 rounded-lg"
                items={calendarDropdownDatasource}
                select={handleCalendarSelection}
                content={selectedCalendar}
              />
              <div className="ms-8">
                {" "}
                {t("Select week timings")}
                <SelectDropDown
                  cssClass="bg-primary_blue px-4 rounded-lg ms-4"
                  items={timingsDropdownDatasource}
                  content={workTimingsForDropDown}
                  select={handleTimingsSelection}
                />
              </div>
            </div>
          )}
          {source === excelImportSources.FILES && !showErrorReview && !importMode && (
            <div className="mt-4">
              <RadioGroup name="uploadOption" value={uploadOption} onChange={(e) => setUploadOption(e.target.value)}>
                <FormControlLabel
                  value="complete"
                  control={<Radio />}
                  label={<span className="text-sm">{t("Complete the Import Process")}</span>}
                />
                <FormControlLabel
                  value="uploadFiles"
                  control={<Radio />}
                  label={<span className="text-sm">{t("Upload Files to match Containers in the next step")}</span>}
                />
                <FormControlLabel
                  value="importDirectly"
                  control={<Radio />}
                  label={<span className="text-sm">{t("Import file directly")}</span>}
                />
              </RadioGroup>
            </div>
          )}
        </div>
      );
    }
  };

  const importFooterContent = () => {
    const getButtonText = () => {
      if (currentTab === "Validate Data") {
        if (source === excelImportSources.FILES && uploadOption === "uploadFiles") {
          return t("Next");
        }
        return t("Confirm");
      }
      return t("Next");
    };

    return (
      <div className="flex gap-4 pt-4 border-t">
        <ButtonComponent
          buttonType={BUTTON_TYPES.GRADIENT_BUTTON}
          loading={buttonLoading}
          onClick={handleNextClick}
          disabled={!enableNextButton && !buttonLoading}
        >
          {getButtonText()}
        </ButtonComponent>

        {currentTab == "Upload File" ? (
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={() => setShowImportModal(false)}>
            {t("Cancel")}
          </ButtonComponent>
        ) : (
          <ButtonComponent buttonType={BUTTON_TYPES.BORDER_BUTTON} onClick={HandleGoBack}>
            {t("Go back")}
          </ButtonComponent>
        )}
      </div>
    );
  };

  const importHeaderContent = () => (
    <div className="w-full">
      <div className="flex justify-between w-full items-center ">
        <div>{t("Import Data")}</div>
      </div>
      <div className="flex items-center justify-center gap-6 mt-4">
        {getHeader("Upload File", 1)}
        {getHeaderLine()}
        {getHeader("Select Header", 2)}
        {getHeaderLine()}
        {getHeader("Match Columns", 3)}
        {getHeaderLine()}
        {getHeader("Validate Data", 4)}
      </div>
    </div>
  );

  const buildCalendarDropdownDatasource = (calendar: XmlCalendar[]) => {
    const temp: XmlDropdownItem[] = [];
    if (xmlSoftware == "P6") {
      calendar.forEach((data, index) => {
        temp.push({ text: data.Name!, id: String(index) });
      });
    } else if (xmlSoftware == "MSP") {
      calendar.forEach((data, index) => {
        if (data.WeekDays) {
          temp.push({ text: data.Name!, id: String(index) });
        }
      });
    }
    setCalendarArray(calendar);
    setCalendarDropdownDatasource(temp);
  };

  const handleConvert = () => {
    try {
      if (file) {
        const reader = new FileReader();
        // The xml branch never populates this — it stays the empty upload — while the csv/xlsx
        // branches replace it with the sheet's positional rows, so it is viewed as both shapes at
        // once. An empty array inhabits that type honestly; `{}` needed an assertion to.
        let json: ParsedUpload = [];
        reader.onload = (e: ProgressEvent<FileReader>) => {
          try {
            const data = e.target!.result;
            const fileExtention = file.name.split(".").pop();
            if (fileExtention === "xml") {
              if (typeof json == "string") {
                dispatch(
                  showToastNotification({
                    content: t("Something went wrong or the given file is not as per our Format."),
                    type: TOAST_TYPE_ERROR,
                  }),
                );
              } else {
                buildCalendarDropdownDatasource(json.calendar ? json.calendar : []);
                setFinalData(json.data!);
                setCurrentTab("Validate Data");
              }
            } else if ((["csv", "xls", "xlsx"] as (string | undefined)[]).includes(fileExtention)) {
              const isCsv = fileExtention === "csv";
              if (isCsv) {
                json = parseCsvText(String(data));
                setAllJsonData(json);
                setCurrentTab("Select Header");
              } else {
                const workbook = XLSX.read(data, { type: "binary", cellDates: true });
                const worksheet = workbook.Sheets[workbook.SheetNames[0]!]!;

                json = XLSX.utils.sheet_to_json(worksheet, {
                  header: 1,
                  blankrows: false,
                  raw: false,
                });
                const holidays: unknown[] = XLSX.utils.sheet_to_json(workbook.Sheets[workbook.SheetNames[1]!]!, {
                  blankrows: false,
                  raw: false,
                });
                setHolidays(holidays);
                setAllJsonData(json);
                setCurrentTab("Select Header");
              }
            } else {
              dispatch(
                showToastNotification({
                  content: t("File format not supported"),
                  type: TOAST_TYPE_ERROR,
                }),
              );
            }
          } catch {
            dispatch(
              showToastNotification({
                content: t("Something went wrong or the given file is not as per our Format."),
                type: TOAST_TYPE_ERROR,
              }),
            );
          }
        };
        reader.readAsBinaryString(file);
      } else {
        dispatch(
          showToastNotification({
            content: t("file not selected"),
            type: TOAST_TYPE_ERROR,
          }),
        );
      }
    } catch {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };

  const submittedContainersRef = useRef<ExcelImportRow[] | null>(null);

  const getIncorrectContainers = (responseData: ContainerImportResult[], rows: ExcelImportRow[] = finalData) => {
    return rows.reduce((array: ExcelImportRow[], container, index) => {
      if (!responseData[index]?.status) {
        const backendError = responseData[index]?.message || "";
        array.push({
          ...container,
          type: backendError,
          // A row's `errors` bag is `unknown` on `ExcelImportRow`; checking it is what the old
          // `|| []` was reaching for, and the assertion made that check statically dead.
          errors: [
            ...(Array.isArray(container.errors) ? container.errors : []),
            ...(backendError ? [backendError] : []),
          ],
        });
      }
      return array;
    }, []);
  };

  const getCorrectContainers = (responseData: ContainerImportResult[], rows: ExcelImportRow[] = finalData) => {
    return rows.reduce((array: CreatedContainer[], container, index) => {
      if (responseData[index]?.status) {
        array.push({
          container,
          response: responseData[index]!,
          index,
        });
      }
      return array;
    }, []);
  };

  // SCHEMA GAP: the five import endpoints share no response schema; the fields the caller reads are
  // named by this return annotation, which each branch's own response type satisfies.
  const handleImportExcel = async (): Promise<ImportResponse | undefined> => {
    let response: ImportResponse | undefined;
    submittedContainersRef.current = null;
    if (source == excelImportSources.ESTIMATE) {
      // `sourceId` is optional on the props (the files flow has none) while this endpoint takes a
      // branded id, so it is parsed rather than branded by assertion. A miss leaves `response`
      // undefined, which the caller already treats as a failed import.
      const estimateId = objectIdString.safeParse(sourceId);
      if (!estimateId.success) return undefined;
      response = await saveEstimateItemsFromExcelSheet({
        estimateId: estimateId.data,
        // TYPE GAP: the import rows are built as untyped bags by `validateEstimateData`
        // (ExcelHelper.ts) while this payload is `Exact<>`-shaped; the shape that actually decides
        // the request is the server's `saveEstimateItemsFromExcelSheetRequest`, which validates it.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- untyped validator output, see above
        items: finalData as typeof finalData & Parameters<typeof saveEstimateItemsFromExcelSheet>[0]["items"],
      });
    } else if (source === excelImportSources.SCHEDULE) {
      response = await importScheduleTasks({
        // TYPE GAP: rows built as untyped bags by `validateScheduleData` (ExcelHelper.ts); the
        // request shape is validated by the server's `importScheduleTasksNewRequest`.

        data: finalData as typeof finalData & Parameters<typeof importScheduleTasks>[0]["data"],
        calendar: {
          // Holidays come either from the workbook's second sheet or from the XML calendar, so only
          // the rows that really carry both dates are sent.
          holidays: (holidays ?? []).filter(isHolidaySpan),
          workWeek,
          workTimings,
        },
      });
    } else if (source === excelImportSources.COST_DATABASE) {
      const costDatabaseId = objectIdString.safeParse(sourceId);
      if (!costDatabaseId.success) return undefined;
      response = await importCostDatabaseItemsFromExcelSheet({
        // TYPE GAP: rows built as untyped bags by `validateEstimateData` (ExcelHelper.ts); the
        // request shape is validated by the server's `saveCostDatabaseItemsFromExcelSheetRequest`.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- untyped validator output, see above
        data: finalData as typeof finalData & Parameters<typeof importCostDatabaseItemsFromExcelSheet>[0]["data"],
        costDatabaseId: costDatabaseId.data,
      });
    } else if (source == excelImportSources.ASSETS) {
      response = await axiosWrapper.post<ImportAssetsFromExcelResponse>(
        `${getBackendUrl()}/assets/importAssetsFromExcel`,
        {
          assetData: finalData,
          projectId: sourceId,
        },
      );
    } else if (source == excelImportSources.FILES) {
      const formData = formDataGenerator(customFields);
      const submittedRows = finalData.filter(
        (row) => !(Array.isArray(row.errors) ? row.errors : []).includes(DOCUMENT_NUMBER_DUPLICATE_IN_IMPORT_ERROR),
      );
      submittedContainersRef.current = submittedRows;
      response = toImportResponse(
        await importExcelContainers({
          data: submittedRows,
          currentData: {
            ...currentData,
            ...formData,
          },
        }),
      );
    }
    return response;
  };

  const formDataGenerator = (customFields: ExcelFormTemplate[]) => {
    const returnObj: Record<string, unknown> = {};
    if (customFields[0]) {
      returnObj.formTemplateId = customFields[0]._id;
      returnObj.formFields = expectedFields.filter((x) => !!x.customFieldId);
    }
    return returnObj;
  };

  const openUploadModal = (correctData: CreatedContainer[]) => {
    const containerFiles = correctData
      .map(({ container, response }) => {
        const fileNameField = t("File Name");
        const fileFormatField = t("File Format");
        const folderNameField = t("Folder Name");
        // Sheet cells are `unknown` on an import row; converting is what the old `as string` was
        // standing in for, and `String()` keeps `|| ""`'s outcome for every falsy cell.
        const containerFileName = String(container[fileNameField] || "");
        const containerFileFormat = container[fileFormatField];
        const containerFolderName = String(container[folderNameField] || "");
        const createdContainerId = response.data?.fileId;
        const revisionLabel = String(container["Revision"] ?? container["revision"] ?? "").trim();
        const revisionSeriesId = container.revisionSeriesId;

        return {
          name: containerFileName,
          fileFormat: containerFileFormat,
          folderPath: containerFolderName,
          hierarchyId: createdContainerId,
          spaceId: currentData?.spaceId,
          revisionLabel,
          revisionSeriesId,
          startPattern: container.startPattern ?? null,
          sequenceType: container.sequenceType ?? null,
          sequenceRules: container.sequenceRules ?? null,
        };
      })
      .filter((f) => f.name && f.hierarchyId);

    const hierarchyId = currentData?.hierarchyId || "0";
    const spaceId = currentData?.spaceId;

    dispatch(
      setFileUploadProperties({
        [`${spaceId}_${hierarchyId}`]: {
          workspaceId: spaceId,
          fileUploadList: [],
        },
      }),
    );

    dispatch(
      setCurrentFileUploadProperties({
        workspaceId: spaceId,
        hierarchyId,
        currentFiles: containerFiles,
        currentFolderRef: {
          hierarchyId,
          name: currentData?.folderName || "/",
          filterPath: currentData?.filterPath || "/",
        },
        isFolderUpload: false,
        isUploadAllowed: true,
        isExcelImportFlow: true,
        excelImportContainers: containerFiles,
        formTemplate: customFields[0]
          ? { template: customFields[0].template ?? [], formTemplateId: customFields[0]._id }
          : { template: [] },
      }),
    );

    dispatch(maximiseFileUpload());
    setShowImportModal(false);
  };

  const openDirectImportModal = async () => {
    const fileNameField = t("File Name");
    const fileFormatField = t("File Format");
    const folderNameField = t("Folder Name");

    let folderStartPattern: string | null = null;
    let folderSequenceType: RevisionSequenceType | null = null;
    let folderSequenceRules: Required<RevisionSequenceRules> | null = null;
    if (currentData?.hierarchyId) {
      const series = await fetchRevisionSeries(currentData.hierarchyId);
      folderStartPattern = series.startPattern;
      folderSequenceType = series.sequenceType ?? null;
      folderSequenceRules = getEffectiveSequenceRules({
        defaultBehavior: series.defaultBehavior ?? undefined,
        sequenceRules: series.sequenceRules ?? undefined,
      });
    }

    const fp = (currentData?.filterPath || "").replace(/^\/+/, "").replace(/\/+$/, "");
    const fn = (currentData?.folderName || "").trim();
    const currentFolderPath = [fp, fn].filter(Boolean).join("/");

    const stripCurrentPath = (folderName: string) => {
      const trimmed = folderName.trim();
      if (currentFolderPath && trimmed.startsWith(currentFolderPath)) {
        return trimmed.slice(currentFolderPath.length).replace(/^\/+/, "");
      }
      return trimmed;
    };

    const containerDefinitions: ContainerDefinition[] = finalData
      .map((container) => {
        // Sheet cells are `unknown` on an import row; `String()` is the conversion the old
        // `as string` claimed, and it keeps `|| ""`'s outcome for every falsy cell.
        const containerFileName = String(container[fileNameField] || "");
        const containerFileFormat = container[fileFormatField];
        const containerFolderName = stripCurrentPath(String(container[folderNameField] || ""));
        const revisionLabel = String(container["Revision"] ?? container["revision"] ?? "").trim();
        const revisionSeriesId = container.revisionSeriesId;

        return {
          name: containerFileName,
          fileFormat: containerFileFormat,
          folderPath: containerFolderName,
          spaceId: currentData?.spaceId,
          containerData: container,
          revisionLabel,
          revisionSeriesId,
        };
      })
      .filter((f) => f.name);

    if (matchByDocumentNumber) {
      const docNumberGroups = new Map<string, ContainerDefinition[]>();
      containerDefinitions.forEach((definition) => {
        const docNumber = extractDocumentNumber(definition.containerData);
        if (!docNumber) return;
        const fileType = String(definition.fileFormat ?? "")
          .trim()
          .toLowerCase();
        const key = `${definition.folderPath}|${docNumber.toLowerCase()}|${fileType}`;
        const group = docNumberGroups.get(key) ?? [];
        group.push(definition);
        docNumberGroups.set(key, group);
      });
      docNumberGroups.forEach((group) => {
        if (group.length < 2) return;
        group.forEach((definition) => {
          // The row's `errors` bag is `unknown` on `ExcelImportRow`; it is seeded when absent so the
          // marker lands on the same array the grid renders.
          const existingErrors = definition.containerData.errors;
          const errors: unknown[] = Array.isArray(existingErrors) ? existingErrors : [];
          definition.containerData.errors = errors;
          if (!errors.includes(DOCUMENT_NUMBER_DUPLICATE_IN_IMPORT_ERROR)) {
            errors.push(DOCUMENT_NUMBER_DUPLICATE_IN_IMPORT_ERROR);
          }
        });
      });
    }

    const hierarchyId = currentData?.hierarchyId || "0";
    const spaceId = currentData?.spaceId;

    const existingFiles: Record<string, unknown>[] = [];
    const folderFilesMap: Record<string, Record<string, unknown>[]> = {};
    const allContainerFolderPaths = containerDefinitions.map((c) => c.folderPath || "");
    const uniqueFolderPaths = [...new Set(allContainerFolderPaths)];
    const currentFolderMarker = "__CURRENT_FOLDER__";
    const currentFolderFiles = await fetchFilesFromFolder(hierarchyId, spaceId!, "this", true);
    currentFolderFiles.forEach((file) => {
      existingFiles.push({
        ...file,
        folderPath: currentFolderMarker,
      });
    });
    folderFilesMap[currentFolderMarker] = currentFolderFiles;
    // `currentData.spaceId` is a plain string on the props, while `ensureFolderPathExists` forwards
    // it into a `createFolder` request that validates it as an ObjectId — so it is parsed here
    // rather than branded. Without a usable space id there is nowhere to create a folder, and the
    // containers stay in the folder the import was started from: the same id
    // `ensureFolderPathExists` itself returns for an empty path.
    const parsedSpaceId = objectIdString.safeParse(spaceId);
    for (const folderPath of uniqueFolderPaths) {
      const targetFolderId = parsedSpaceId.success
        ? await ensureFolderPathExists(folderPath, hierarchyId, parsedSpaceId.data)
        : hierarchyId;
      if (targetFolderId === hierarchyId) {
        folderFilesMap[folderPath] = currentFolderFiles;
        currentFolderFiles.forEach((file) => {
          existingFiles.push({
            ...file,
            folderPath: folderPath,
          });
        });
        continue;
      }

      const folderFiles = await fetchFilesFromFolder(targetFolderId, spaceId!, "this", true);
      folderFiles.forEach((file) => {
        existingFiles.push({
          ...file,
          folderPath: folderPath,
        });
      });
      folderFilesMap[folderPath] = folderFiles;
    }

    let docNumberQuestionId: string | null = null;
    if (matchByDocumentNumber) {
      for (const templateDoc of customFields) {
        for (const section of templateDoc.template ?? []) {
          const question = (section.questionFields ?? []).find((q) => isDocumentNumberField(q.questionValue as string));
          if (question?._id) docNumberQuestionId = String(question._id);
        }
      }
    }

    containerDefinitions.forEach((c) => {
      const folderKey = c.folderPath || currentFolderMarker;
      const filesInFolder = folderFilesMap[folderKey] ?? folderFilesMap[currentFolderMarker] ?? [];
      const fullName = `${c.name}.${c.fileFormat}`.toLowerCase();
      let existing = filesInFolder.find((f) => String(f.name || "").toLowerCase() === fullName);
      if (matchByDocumentNumber && docNumberQuestionId) {
        // Held in a const so the narrowing survives into the callback below, where a mutable `let`
        // from the enclosing scope would be back to `string | null`.
        const questionId = docNumberQuestionId;
        const docNumber = extractDocumentNumber(c.containerData)?.toLowerCase();
        if (docNumber) {
          const rowFileType = String(c.fileFormat ?? "")
            .trim()
            .toLowerCase();
          const docMatches = filesInFolder.filter((f) => {
            // A file's `customFields` bag is `unknown` here and its keys are runtime question ids.
            const customFieldsBag = isRecord(f.customFields) ? f.customFields : undefined;
            const value = String(customFieldsBag?.[questionId] ?? "")
              .trim()
              .toLowerCase();
            if (!value || value !== docNumber) return false;
            const fileType =
              getFileExtension(String(f.name ?? "")) ||
              String(f.fileType ?? "")
                .trim()
                .toLowerCase();
            return fileType === rowFileType;
          });
          existing = docMatches.length === 1 ? docMatches[0] : undefined;
        }
      }
      c.previousLabel = readLabel(existing?.lastMarkedRevisionSeries) || readLabel(existing?.revisionSeries) || null;
      c.existingLabels = Array.isArray(existing?.allRevisionLabels) ? existing.allRevisionLabels : [];
      c.startPattern = folderStartPattern;
      c.sequenceType = folderSequenceType;
      c.sequenceRules = folderSequenceRules;
    });

    dispatch(
      setFileUploadProperties({
        [`${spaceId}_${hierarchyId}`]: {
          workspaceId: spaceId,
          fileUploadList: [],
        },
      }),
    );

    const formData = formDataGenerator(customFields);

    dispatch(
      setCurrentFileUploadProperties({
        workspaceId: spaceId,
        hierarchyId,
        currentFiles: existingFiles,
        currentFolderRef: {
          hierarchyId,
          name: currentData?.folderName || "/",
          filterPath: currentData?.filterPath || "/",
        },
        isFolderUpload: false,
        isUploadAllowed: true,
        isDirectImportFlow: true,
        directImportContainers: containerDefinitions,
        directImportCurrentData: {
          ...currentData,
          ...formData,
        },
        // TYPE GAP: the reducer declares this map's values as `string[]`, but the direct-import flow
        // stores the folder's file RECORDS here (`fetchFilesFromFolder` returns
        // `Record<string, unknown>[]`, and FileUploadNew reads them back as records). The fix is
        // `directImportFolderFilesMap?: { [k: string]: Record<string, unknown>[] }` in
        // reducers/minimiseFileUpload.ts; asserted at the boundary until then, value unchanged.
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion -- reducer declares `string[]`, see above
        directImportFolderFilesMap: folderFilesMap as typeof folderFilesMap &
          Parameters<typeof setCurrentFileUploadProperties>[0]["directImportFolderFilesMap"],
        formTemplate: customFields[0]
          ? { template: customFields[0].template ?? [], formTemplateId: customFields[0]._id }
          : { template: [] },
      }),
    );

    dispatch(maximiseFileUpload());
    setShowImportModal(false);
  };

  const hasMissingColumnInHeader = () => {
    if ([excelImportSources.ESTIMATE, excelImportSources.COST_DATABASE, excelImportSources.SCHEDULE].includes(source)) {
      return allColumnNames.length !== requiredFields.length;
    } else {
      const baseRequiredFields = expectedFields.filter((field) => !field.customFieldId);
      const requiredFieldNames = baseRequiredFields.map((field) => field.headerText.toLowerCase().trim());
      const selectedColumnNames = allColumnNames.map((col) => col.toLowerCase().trim());
      const hasMissingColumns = requiredFieldNames.some((name) => !selectedColumnNames.includes(name));
      return hasMissingColumns || allColumnNames.length < baseRequiredFields.length;
    }
  };

  const handleNextClick = async () => {
    if (currentTab == "Upload File") {
      setButtonLoading(true);
      handleConvert();
      setEnableNextButton(false);
      setButtonLoading(false);
    } else if (currentTab == "Select Header") {
      const hasMissingColumn = hasMissingColumnInHeader();

      if (hasMissingColumn) {
        dispatch(
          showToastNotification({
            content: t("Selected header row has some missing column name."),
            type: TOAST_TYPE_WARNING,
          }),
        );
        return;
      }
      setCurrentTab("Match Columns");
      setEnableNextButton(false);
      autoMatching();
    } else if (currentTab == "Match Columns") {
      setEnableNextButton(false);
      setButtonLoading(true);
      await validatorFunction();
      setCurrentTab("Validate Data");
    } else if (currentTab == "Validate Data") {
      setButtonLoading(true);
      setEnableNextButton(false);

      try {
        if (source === excelImportSources.FILES) {
          if (finalData.length > MAX_EXCEL_IMPORT_CONTAINERS) {
            dispatch(
              showToastNotification({
                content: t("You can import a maximum of {{count}} rows at a time.", {
                  count: MAX_EXCEL_IMPORT_CONTAINERS,
                }),
                type: TOAST_TYPE_WARNING,
              }),
            );
            return;
          }
          if (uploadOption === "uploadFiles") {
            if (showErrorReview && validContainers.length > 0) {
              openUploadModal(validContainers);
              dispatch(
                showToastNotification({
                  content: t("{{validContainers_length}} container(s) ready. Now upload files to match them.", {
                    validContainers_length: validContainers.length,
                  }),
                  type: TOAST_TYPE_SUCCESS,
                }),
              );
              setButtonLoading(false);
              return;
            }

            const res = (await handleImportExcel())!;
            if (res.data.status) {
              const arr = res.data.data;
              const submittedRows: ExcelImportRow[] = submittedContainersRef.current ?? finalData;
              const withheldRows: ExcelImportRow[] = finalData.filter((row) => !submittedRows.includes(row));
              const incorrectData: ExcelImportRow[] = [...getIncorrectContainers(arr!, submittedRows), ...withheldRows];
              const correctData = getCorrectContainers(arr!, submittedRows);

              if (correctData.length > 0) {
                setValidContainers(correctData);

                if (incorrectData.length > 0) {
                  setFinalData(incorrectData);
                  setShowErrorReview(true);
                  setEnableNextButton(true);
                  dispatch(
                    showToastNotification({
                      content: t(
                        "{{correctData_length}} container(s) created. {{incorrectData_length}} had issues and were skipped.",
                        { correctData_length: correctData.length, incorrectData_length: incorrectData.length },
                      ),
                      type: TOAST_TYPE_WARNING,
                    }),
                  );
                  setButtonLoading(false);
                  return;
                } else {
                  openUploadModal(correctData);
                  dispatch(
                    showToastNotification({
                      content: t("Containers created successfully. Now upload files to match them."),
                      type: TOAST_TYPE_SUCCESS,
                    }),
                  );
                  setButtonLoading(false);
                  return;
                }
              } else {
                setFinalData(incorrectData);
                setEnableNextButton(true);
                dispatch(
                  showToastNotification({
                    content: t("All containers have issues. Please fix them before uploading files."),
                    type: TOAST_TYPE_ERROR,
                  }),
                );
                setButtonLoading(false);
                return;
              }
            } else {
              dispatch(
                showToastNotification({
                  content: untranslated(res.data.message!) || SOMETHING_WENT_WRONG,
                  type: TOAST_TYPE_ERROR,
                }),
              );
              setEnableNextButton(true);
              setButtonLoading(false);
              return;
            }
          } else if (uploadOption === "importDirectly") {
            await openDirectImportModal();
            dispatch(
              showToastNotification({
                content: t(
                  "Upload files to match with containers from Excel. Matching files will be uploaded directly.",
                ),
                type: TOAST_TYPE_SUCCESS,
              }),
            );
            setButtonLoading(false);
            return;
          } else if (uploadOption === "complete") {
            if (showErrorReview) {
              setShowImportModal(false);
              setButtonLoading(false);
              refreshDataGrid(true);
              return;
            }

            const res = (await handleImportExcel())!;
            const toastData = {
              content: SOMETHING_WENT_WRONG,
              type: TOAST_TYPE_ERROR,
            };
            if (res.data.status) {
              const arr = res.data.data;
              const submittedRows: ExcelImportRow[] = submittedContainersRef.current ?? finalData;
              const withheldRows: ExcelImportRow[] = finalData.filter((row) => !submittedRows.includes(row));

              if (arr!.length == submittedRows.length) {
                const incorrectData: ExcelImportRow[] = [
                  ...getIncorrectContainers(arr!, submittedRows),
                  ...withheldRows,
                ];
                const correctData = getCorrectContainers(arr!, submittedRows);

                if (incorrectData.length) {
                  setFinalData(incorrectData);
                  setShowErrorReview(true);
                  setEnableNextButton(true);
                  toastData.type = TOAST_TYPE_WARNING;
                  toastData.content = t(
                    "{{correct}} container(s) imported successfully. {{incorrect}} had issues and were skipped.",
                    {
                      correct: correctData.length,
                      incorrect: incorrectData.length,
                    },
                  );
                  refreshDataGrid(true);
                } else {
                  setShowImportModal(false);
                  toastData.type = TOAST_TYPE_SUCCESS;
                  toastData.content = t("Imported Successfully!");
                  refreshDataGrid(true);
                }
              } else {
                setShowImportModal(false);
              }
            } else {
              setShowImportModal(false);
            }
            dispatch(showToastNotification(toastData));
            setButtonLoading(false);
            return;
          }
        }

        const res = (await handleImportExcel())!;
        if (res.data.status) {
          const message =
            source == "assets"
              ? `${res.data.insertedCount} Assets imported Successfully`
              : t("Imported Successfully !!");
          setShowImportModal(false);
          dispatch(
            showToastNotification({
              content: untranslated(message),
              type: TOAST_TYPE_SUCCESS,
            }),
          );
          if (
            [excelImportSources.ESTIMATE, excelImportSources.COST_DATABASE, excelImportSources.ASSETS].includes(source)
          ) {
            refreshDataGrid(true, res.data.insertedCount);
          } else {
            window.location.reload();
          }
        } else {
          dispatch(
            showToastNotification({
              content: untranslated((res.data.message || res.data.errorMessage)!),
              type: TOAST_TYPE_ERROR,
            }),
          );
        }
      } catch {
        dispatch(
          showToastNotification({
            content: SOMETHING_WENT_WRONG,
            type: TOAST_TYPE_ERROR,
          }),
        );
      } finally {
        setButtonLoading(false);
        setEnableNextButton(true);
      }
    }
  };

  const HandleGoBack = () => {
    if (currentTab == "Select Header") {
      setCurrentTab("Upload File");
      if (file) setEnableNextButton(true);
    } else if (currentTab == "Match Columns") {
      setColumnMapper({});
      setCurrentTab("Select Header");
      setEnableNextButton(false);
    } else if (currentTab == "Validate Data") {
      if (isXml) {
        setCurrentTab("Upload File");
      } else {
        setCurrentTab("Match Columns");
      }
    }
  };

  return (
    <ModalStateDialogComponent
      height="80%"
      allowModalPadding={false}
      footerContent={importFooterContent()}
      width="80%"
      onCancel={() => setShowImportModal(false)}
      headerTitle={importHeaderContent()}
      isVisible={showImportModal}
      modelContent={importModalContent()}
    />
  );
}
export default ExcelImport;
